import { StatusBar, Modal, PrimaryButton, SecondaryButton, SnackbarStatus } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './SyncReportModal.module.scss';
import chefPlating from 'src/assets/images/chef-plating.png';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { commitSync } from 'src/api/commitSync/commitSync';
import { useQueryClient } from '@tanstack/react-query';
import { SyncModalHeaders } from './SyncModalHeaders/SyncModalHeaders';
import { SyncModalItemSubHeaders } from './SyncModalItemSubHeaders/SyncModalItemSubHeaders';
import { SyncProductList } from './SyncProductList/SyncProductList';
import { SyncAcknowledgeChangeCheckBox } from './SyncAcknowledgeChangeCheckBox/SyncAcknowledgeChangeCheckBox';
import { IProductOverrides } from 'src/api/commitSync/types';
import { EProductLibraryStatus, ISyncProduct } from '../../api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { IProductLibraryStatus } from '../../hooks/useProductLibraryStatus/useProductLibraryStatus';
import { QueryKeys, SYNC_STATUS_QUERY } from '../../hooks/QueryKeys';
import { useEvents } from '../../hooks/useEvents';
import { NotificationStatus } from 'src/api/getEvents';
import { ItemType } from '@weareopenr/types';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import { IIngestDiffSummary } from 'src/api/getIngestDiff';

export const productDataKeys: string[] = ['inserts', 'updates', 'deletes'];
export const pageTitles = ['New Products', 'Updated Products', 'Removed Products'];

interface ISyncDiff {
  inserts: ISyncProduct[];
  updates: ISyncProduct[];
  deletes: ISyncProduct[];
}

interface ISyncReportModalProps {
  isOpen: boolean;
  products?: ISyncDiff;
  closeModal: () => void;
  rejectSync: () => void;
  /**
   * Supplied for syncs that ran through service-ingest. Those commit via `/ingest/commit`, which
   * applies the whole diff and takes no per-product overrides, so the modifier column is hidden and
   * the accept button defers to this instead of calling the legacy commit endpoint.
   */
  onAccept?: () => Promise<void>;
  /** Counts for entities the item-level diff cannot show. See {@link buildIngestSummaryMessage}. */
  ingestSummary?: IIngestDiffSummary | null;
}

interface AdditionalProps extends Omit<ISyncReportModalProps, 'isOpen'> {
  stepCount: number;
  setStepCount: Dispatch<SetStateAction<number>>;
  hasScheduledEvent: boolean;
}

const SCHEDULED_EVENT_NOTE = (
  <span>
    <strong>Quick note</strong> - You have upcoming schedules. New products will need a price, POS ID, and tax rate
    before they can be published. Please ensure all data is present to prevent publish failure.
  </span>
);

const pluralise = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The product list above only covers items. An ingest sync also writes option groups and modifiers,
 * and accepting commits those too, so their counts are stated before the user accepts.
 */
export const buildIngestSummaryMessage = (summary?: IIngestDiffSummary | null): string | null => {
  if (!summary) return null;

  const parts = (
    [
      ['optionGroups', 'option group'],
      ['options', 'modifier'],
    ] as const
  ).flatMap(([key, noun]) => {
    const counts = summary[key];
    if (!counts) return [];
    const total = counts.new + counts.updated + counts.deleted;
    if (total === 0) return [];
    return [`${pluralise(total, noun)} (${counts.new} new, ${counts.updated} updated, ${counts.deleted} removed)`];
  });

  return parts.length ? `This sync also changes ${parts.join(' and ')}, which are not listed above.` : null;
};

const Content = ({
  products,
  closeModal,
  rejectSync,
  stepCount,
  setStepCount,
  hasScheduledEvent,
  onAccept,
  ingestSummary,
}: AdditionalProps) => {
  const queryClient = useQueryClient();
  const [isChangeAccepted, setIsChangeAccepted] = useState<boolean>(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { markNotificationRead } = useUpdateNotification();

  useEffect(() => {
    setIsChangeAccepted(false);
  }, [stepCount]);

  const productKey: string = productDataKeys[stepCount];
  // Ingest syncs supply no legacy diff, so each step renders its empty state rather than crashing.
  const productsListByKeyName: ISyncProduct[] = products?.[productKey] ?? [];
  const showModifierCheckbox: boolean = !onAccept && productKey === productDataKeys[0];
  const isLastStep = stepCount === productDataKeys.length - 1;
  const ingestSummaryMessage = buildIngestSummaryMessage(ingestSummary);

  const acceptChanges = () => {
    setIsChangeAccepted((prev) => !prev);
  };

  const reject = () => {
    rejectSync();
    closeModal();
  };
  const stepNext = () => {
    if (!isLastStep) {
      setStepCount(stepCount + 1);
    }
  };

  const commitSyncSubmit = async (selectedProductIds: string[]) => {
    clearError();
    setIsSubmitting(true);
    try {
      // Ingest syncs are committed by the caller's hook, which owns the cache updates too.
      if (onAccept) {
        await onAccept();
        closeModal();
        return;
      }

      const formattedPayload: IProductOverrides[] = selectedProductIds.map((itemId: string) => ({
        item_id: itemId,
        type: ItemType.MODIFIER,
      }));

      await commitSync(formattedPayload);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.priceSchemes] });
      // set sync status to PENDING
      queryClient.setQueryData(SYNC_STATUS_QUERY, (old: IProductLibraryStatus) => ({
        ...old,
        sync_status: EProductLibraryStatus.PENDING,
      }));

      markNotificationRead((notification) => notification.type === NotificationType.PRODUCT_SYNC);

      closeModal();
    } catch (error) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'An error has occurred; sync changes could not be accepted.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckedProduct = (event, productId) => {
    if (event.currentTarget.checked) {
      if (!selectedProductIds.includes(productId)) {
        setSelectedProductIds([...selectedProductIds, productId]);
      }
    } else {
      setSelectedProductIds(selectedProductIds.filter((itemProductId) => itemProductId !== productId));
    }
  };

  return (
    <div>
      <SyncModalHeaders stepCount={stepCount} pageTitle={pageTitles[stepCount]} />
      <SyncModalItemSubHeaders showCheckbox={showModifierCheckbox} />
      <SyncProductList
        productListData={productsListByKeyName}
        handleCheckedProduct={handleCheckedProduct}
        selectedProducts={selectedProductIds}
        showCheckbox={showModifierCheckbox}
        stepCount={stepCount}
      />
      <SyncAcknowledgeChangeCheckBox
        acknowledgeChange={isChangeAccepted}
        acceptChanges={acceptChanges}
        productsCount={productsListByKeyName.length}
        stepCount={stepCount}
      />

      {hasScheduledEvent && stepCount === 0 && <StatusBar message={SCHEDULED_EVENT_NOTE} variant="info" />}
      {isLastStep && ingestSummaryMessage && <StatusBar message={ingestSummaryMessage} variant="info" />}
      <ButtonContainer
        left={
          <SecondaryButton data-testid="reject-button" buttonStyle="outline" onClick={reject} size="full-width">
            Reject Sync
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            onClick={!isLastStep ? stepNext : () => commitSyncSubmit(selectedProductIds)}
            size="full-width"
            disabled={!isChangeAccepted || isSubmitting}
            data-testid="next-accept-button"
            loading={isSubmitting}
          >
            {!isLastStep ? 'Next' : 'Accept Sync'}
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </div>
  );
};

export const SyncReportModal = ({
  isOpen,
  products,
  closeModal,
  rejectSync,
  onAccept,
  ingestSummary,
}: ISyncReportModalProps) => {
  const [stepCount, setStepCount] = useState<number>(0);
  const { data: scheduledEvents } = useEvents({ status: NotificationStatus.SCHEDULED }, { enabled: isOpen });
  const hasScheduledEvent = !!scheduledEvents?.data?.length;

  useEffect(() => {
    if (!isOpen) {
      setStepCount(0);
    }
  }, [isOpen]);

  const stepBack = () => {
    if (stepCount > 0) {
      setStepCount(stepCount - 1);
    }
  };

  return (
    <Modal
      // Legacy syncs carry their diff on the sync status response and there is nothing to review
      // without it. Ingest syncs never populate it, so `onAccept` stands in as the evidence that
      // there is a sync to review.
      open={isOpen && (!!products || !!onAccept)}
      closeModal={closeModal}
      image={chefPlating}
      imageAlt="chef"
      onBack={stepCount > 0 ? stepBack : undefined}
    >
      <Content
        products={products}
        closeModal={closeModal}
        rejectSync={rejectSync}
        stepCount={stepCount}
        setStepCount={setStepCount}
        hasScheduledEvent={hasScheduledEvent}
        onAccept={onAccept}
        ingestSummary={ingestSummary}
      />
    </Modal>
  );
};
